<template>
  <article class="payment">
    <div class="payment__container">
      <header>
        <i18n-t keypath="title.select_payment_method" tag="h1">
          <template #method>
            <span>{{ t('title.payment_method') }}</span>
          </template>
        </i18n-t>
      </header>
      <main>
        <div class="payment-dutch" v-if="configStore.config.IsDutchUsed">
          <div class="payment-dutch__icon"><img src="@/assets/images/icon-plus.svg" /></div>
          <button
            type="button"
            class="payment-dutch__button"
            @mouseup="handleDutchClick"
            @touchend.prevent="handleDutchClick"
          >
            <span class="payment-dutch__figure">
              <img src="@/assets/images/icon-payment-dutch.svg" alt="더치페이 이미지" />
            </span>
            <span class="payment-dutch__text">{{ t('button.pay_dutch') }}</span>
          </button>
        </div>
        <ul class="payment-buttons">
          <li>
            <BasePaymentButton @button-click="handleCardClick">
              <template #icon><img src="@/assets/images/icon-payment-card.svg" alt="카드 이미지" /></template>
              {{ t('button.pay_card') }}
            </BasePaymentButton>
          </li>
          <li v-if="configStore.getIsCashPaymentUsed()">
            <BasePaymentButton @button-click="handleCashClick">
              <template #icon><img src="@/assets/images/icon-payment-cash.svg" alt="현금 결제 이미지" /></template>
              {{ t('button.pay_cash') }}
              <template v-if="configStore.config.CashPaymentDescription" #desc>
                {{ configStore.config.CashPaymentDescription }}
              </template>
            </BasePaymentButton>
          </li>
          <li v-if="configStore.config.IsEasyPaymentUsed">
            <BasePaymentButton @button-click="handleEasyClick">
              <template #icon><img src="@/assets/images/icon-payment-easy.png" alt="간편 결제 이미지" /></template>
              {{ t('button.pay_easy') }}
            </BasePaymentButton>
          </li>
          <li v-if="configStore.config.IsCustomerUsed">
            <BasePaymentButton @button-click="handleMixedClick">
              <template #icon><img src="@/assets/images/icon-payment-mixed.png" alt="복합결제 이미지" /></template>
              {{ t('button.pay_mixed') }}
            </BasePaymentButton>
          </li>
        </ul>
      </main>
      <footer>
        <div class="footer_button">
          <BaseButton class="outline" @button-click="handleCancelClick">
            <template #icon>
              <svg xmlns="http://www.w3.org/2000/svg" width="29.442" height="25.247" viewBox="0 0 29.442 25.247">
                <path
                  id="arrow-left-solid"
                  d="M.641,75.063a2.106,2.106,0,0,0,0,2.977L11.157,88.556a2.105,2.105,0,0,0,2.977-2.977L7.2,78.652H27.364a2.1,2.1,0,1,0,0-4.206H7.207l6.921-6.927a2.105,2.105,0,1,0-2.977-2.977L.635,75.057Z"
                  transform="translate(-0.025 -63.925)"
                />
              </svg>
            </template>
            {{ t('button.prev') }}
          </BaseButton>
        </div>
      </footer>
    </div>
  </article>
</template>

<script lang="ts" setup>
  import BaseButton from '@/components/base/BaseButton.vue';
  import OrderComplete from '@/components/OrderComplete.vue';
  import PaymentDutchAmount from '@/components/PaymentDutchAmount.vue';
  import PaymentDutchMenu from '@/components/PaymentDutchMenu.vue';
  import PaymentSplit from '@/components/PaymentSplit.vue';
  import { useOrder } from '@/composable/useOrder';
  import { usePayment } from '@/composable/usePayment';
  import { PaymentMethod } from '@/enums/PaymentMethod';
  import PaymentSummary from '@/interfaces/PaymentSummary';
  import { useCartStore } from '@/stores/cart';
  import { useCommonStore } from '@/stores/common';
  import { useConfigStore } from '@/stores/config';
  import { useModalStore } from '@/stores/modal';
  import { useOrderStore } from '@/stores/order';
  import { usePaymentStore } from '@/stores/payment';
  import { onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import BasePaymentButton from './base/BasePaymentButton.vue';
  import PaymentCardModal from './PaymentCardModal.vue';
  import PaymentEasyModal from './PaymentEasyModal.vue';

  const props = defineProps<{
    /** 선주문후결제 주문 여부 */
    isPreOrder: boolean;
    /** 결제 금액 데이터 */
    paymentSummary: PaymentSummary;
  }>();

  const { t } = useI18n();
  const configStore = useConfigStore();
  const commonStore = useCommonStore();
  const modalStore = useModalStore();
  const cartStore = useCartStore();
  const orderStore = useOrderStore();
  const paymentStore = usePaymentStore();

  const { openReceiptOrComplete } = useOrder();
  const { createPaymentSplit } = usePayment();

  // 버튼이 중복으로 클릭되지 않게 하기 위한 플래그
  const isPaying = ref(false);

  onMounted(() => {
    orderStore.setIsPreOrder(props.isPreOrder);
    paymentStore.setSummary(props.paymentSummary);

    // 현금결제와 더치페이 결제를 사용하지 않고 카드결제만 사용한다면 바로 카드 결제 진행
    if (
      !configStore.getIsCashPaymentUsed() &&
      !configStore.config.IsDutchUsed &&
      !configStore.config.IsEasyPaymentUsed &&
      !configStore.config.IsCustomerUsed
    ) {
      processCardPayment();
    }
  });

  /** 카드 결제 버튼 클릭 */
  const handleCardClick = () => processCardPayment();

  /** 현금 결제 버튼 클릭 */
  const handleCashClick = () => processCashPayment();

  /** 간편 결제 버튼 클릭 */
  const handleEasyClick = () => processEasyPayment();

  /** 복합 결제 버튼 클릭 */
  const handleMixedClick = () => {
    // 복합 결제 페이지로 이동
    modalStore.openFullScreenModal(PaymentSplit, {});
  };

  /** 더치페이 버튼 클릭 */
  const handleDutchClick = () => {
    if (isPaying.value) return;
    isPaying.value = true;

    if (props.isPreOrder) {
      // 선주문후결제인 경우 금액 나눔만 가능하기 때문에 바로 금액 나눔 결제 페이지로 이동
      modalStore.openFullScreenModal(PaymentDutchAmount, {});
    } else {
      // 선결제인 경우 상품 나눔 결제 페이지로 이동
      modalStore.openFullScreenModal(PaymentDutchMenu, {});
    }
  };

  /** 취소 버튼 클릭 */
  const handleCancelClick = () => {
    // 전체와면 모달 닫기
    modalStore.closeFullScreenModal();
  };

  /** 카드 결제 진행 */
  const processCardPayment = async () => {
    if (isPaying.value) return;

    const loadingId = commonStore.showLoading();
    try {
      isPaying.value = true;

      // 카드 결제 진행 후 성공 여부 반환
      const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentCardModal, boolean>(PaymentCardModal, {});

      // 카드 결제 실패한 경우 종료
      if (!isSuccess) return;

      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(true);

      // 선결제인 경우에만 장바구니 데이터를 주문 데이터에 추가
      if (!props.isPreOrder) {
        orderStore.addOrderItem(cartStore.cart.CartMenus);
      }

      // 주문 전송
      await orderStore.sendOrder(true);
    } finally {
      isPaying.value = false;
      commonStore.hideLoading(loadingId);
    }

    // 영수증 또는 주문완료 모달 열기
    openReceiptOrComplete(false);
  };

  /** 현금 결제 진행 */
  const processCashPayment = async () => {
    if (isPaying.value) return;

    const loadingId = commonStore.showLoading();
    try {
      isPaying.value = true;

      const isOK = await commonStore.showDialog({
        type: 'confirm',
        title: t('label.take_order'),
        message: t('general.cash_pay_message'),
      });
      if (!isOK) return;

      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(false);

      // 장바구니 데이터를 주문 데이터에 추가
      orderStore.addOrderItem(cartStore.cart.CartMenus);

      // 현금 결제 데이터 세팅
      paymentStore.addPaymentSplit(createPaymentSplit(PaymentMethod.Cash));

      // 주문 전송
      await orderStore.sendOrder(false);

      // 주문 완료 페이지로 이동 (현금 결제는 수기로 하기 때문에 영수증 페이지로 이동할 필요 없음)
      modalStore.openFullScreenModal(OrderComplete, { hasPayment: false });
    } finally {
      isPaying.value = false;
      commonStore.hideLoading(loadingId);
    }
  };

  /** 간편 결제 진행 */
  const processEasyPayment = async () => {
    if (isPaying.value) return;

    const loadingId = commonStore.showLoading();
    try {
      isPaying.value = true;

      // 간편 결제 진행 후 성공 여부 반환
      const isSuccess = await modalStore.openSyncModalAsync<typeof PaymentEasyModal, boolean>(PaymentEasyModal, {});

      // 간편 결제 실패한 경우 종료
      if (!isSuccess) return;

      // POS에서 주문 조회 후 세팅
      await orderStore.fetchOrder(true);

      // 선결제인 경우에만 장바구니 데이터를 주문 데이터에 추가
      if (!props.isPreOrder) {
        orderStore.addOrderItem(cartStore.cart.CartMenus);
      }

      // 주문 전송
      await orderStore.sendOrder(true);

      // 영수증 또는 주문완료 모달 열기
      openReceiptOrComplete(false);
    } finally {
      isPaying.value = false;
      commonStore.hideLoading(loadingId);
    }
  };
</script>

<style lang="scss" scoped>
  @use '@/assets/scss/variables' as vars;

  .payment {
    display: flex;
    flex-direction: column;
    justify-content: center;
    height: 100%;
    background-color: #f3f4f7;

    .payment__container {
      display: grid;
      grid-template-rows: auto 1fr auto;
      padding: 2.9851vw;
      gap: 4.6875vw;
      @media (max-width: vars.$sm) {
        padding: 20px;
        height: 100%;
      }
      header {
        h1 {
          text-align: center;
          font-size: 3.7313vw;
          font-weight: 700;
          @media (max-width: vars.$sm) {
            font-size: 28px;
          }
          span {
            color: var(--point-color-secondary);
          }
        }
      }

      main {
        display: grid;
        grid-template-rows: auto 1fr;
        grid-template-columns: 1fr;
        justify-content: center;
        align-items: center;
        gap: 3.125vw;
        @media (max-width: vars.$sm) {
          gap: 10px;
          overflow-y: auto;
        }

        .payment-dutch {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          gap: 0.7813vw;
          margin-top: 1.5625vw;

          .payment-dutch__button {
            display: flex;
            align-items: center;
            gap: 0.7813vw;
            padding: 0 2.3438vw;
            height: 6.25vw;
            border-radius: 6.25vw;
            border: none;
            background-color: #000;
            color: #fff;
            font-size: 2.0896vw;

            @media (max-width: vars.$sm) {
              font-size: 20px;
              height: 50px;
            }

            .payment-dutch__figure {
              img {
                width: 4.7917vw;
                height: 4.7917vw;
                @media (max-width: vars.$sm) {
                  width: 40px;
                  height: 40px;
                }
              }
            }
          }
        }

        .payment-buttons {
          display: grid;
          grid-auto-flow: column;
          justify-content: center;
          align-items: center;
          gap: 1.5625vw;
          width: 100%;
          @media (max-width: vars.$sm) {
            gap: 20px;
            grid-auto-flow: unset;
            grid-template-columns: 1fr;
            padding: 10px;
            max-width: 400px;
            margin: 0 auto;
          }

          li {
            width: 21.875vw;
            height: 23.4375vw;
            @media (max-width: vars.$sm) {
              width: 100%;
              height: auto;
            }
          }
        }
      }

      footer {
        display: flex;
        justify-content: center;
        width: 100%;
        .footer_button {
          width: 29.8507vw;
          @media (max-width: vars.$sm) {
            width: 100%;
          }
        }
      }
    }
  }
</style>
